import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
// Legacy Auth page removed — login lives inside the landing page modals
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RoleProtectedRoute } from "@/components/RoleProtectedRoute";
import { RootGate } from "@/components/RootGate";
import { TourGuideProvider, TourTooltip } from "@/components/tour-guide";
import { useUserRole } from "@/hooks/useUserRole";
import { lazy, Suspense } from "react";

// Lazy-loaded route components
const Dashboard = lazy(() => import("@/pages/Dashboard").then(m => ({ default: m.Dashboard })));
const Trades = lazy(() => import("@/pages/Trades").then(m => ({ default: m.Trades })));
const TradeDetail = lazy(() => import("@/pages/TradeDetail").then(m => ({ default: m.TradeDetail })));
const Analytics = lazy(() => import("@/pages/Analytics").then(m => ({ default: m.Analytics })));
const EconomicCalendar = lazy(() => import("@/pages/EconomicCalendar").then(m => ({ default: m.EconomicCalendar })));
const MarketIntelligencePage = lazy(() => import("@/pages/MarketIntelligence").then(m => ({ default: m.MarketIntelligence })));
const Training = lazy(() => import("@/pages/Training").then(m => ({ default: m.Training })));
const TrainingDetail = lazy(() => import("@/pages/TrainingDetail").then(m => ({ default: m.TrainingDetail })));
const TradingPlanEditor = lazy(() => import("@/components/trading-plan/TradingPlanEditor").then(m => ({ default: m.TradingPlanEditor })));
const BlueprintEditor = lazy(() => import("@/components/blueprint/BlueprintEditor").then(m => ({ default: m.BlueprintEditor })));
const JrxStrategyProducerPage = lazy(() => import("@/pages/JrxStrategyProducerPage").then(m => ({ default: m.JrxStrategyProducerPage })));
const UserSetupDetail = lazy(() => import("@/pages/UserSetupDetail").then(m => ({ default: m.UserSetupDetail })));
const BehaviorProfileEditor = lazy(() => import("@/components/behavior/BehaviorProfileEditor").then(m => ({ default: m.BehaviorProfileEditor })));
const ExecutionProfileEditor = lazy(() => import("@/components/execution/ExecutionProfileEditor").then(m => ({ default: m.ExecutionProfileEditor })));
const RiskProfileEditor = lazy(() => import("@/components/risk/RiskProfileEditor").then(m => ({ default: m.RiskProfileEditor })));
const TradeBlueprintPage = lazy(() => import("@/pages/TradeBlueprintPage"));
const Fundamentals = lazy(() => import("@/pages/Fundamentals").then(m => ({ default: m.Fundamentals })));
const TradeAssistant = lazy(() => import("@/pages/TradeAssistant").then(m => ({ default: m.TradeAssistant })));
const PublicProfile = lazy(() => import("@/pages/PublicProfile").then(m => ({ default: m.PublicProfile })));
const Checkout = lazy(() => import("@/pages/Checkout"));


// Admin Workspace
const AdminWorkspaceLayout = lazy(() => import("@/components/admin/AdminWorkspaceLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("@/pages/admin/AdminUserDetail"));
const CommercialCenter = lazy(() => import("@/pages/admin/CommercialCenter"));
const CommercialOverview = lazy(() => import("@/pages/admin/CommercialOverview"));
const CommercialPromotions = lazy(() => import("@/pages/admin/CommercialCodes"));
const CommercialPromoForm = lazy(() => import("@/pages/admin/CommercialCodeForm"));
const CommercialVouchers = lazy(() => import("@/pages/admin/CommercialVouchers"));
const CommercialRedemptions = lazy(() => import("@/pages/admin/CommercialRedemptions"));
const CommercialReferrals = lazy(() => import("@/pages/admin/CommercialReferrals"));
const CommercialLocalPayments = lazy(() => import("@/pages/admin/CommercialLocalPayments"));
const CommercialDiscounts = lazy(() => import("@/pages/admin/CommercialDiscounts"));
const AdminBilling = lazy(() => import("@/pages/admin/AdminBilling"));
const AdminProduct = lazy(() => import("@/pages/admin/AdminProduct"));
const AdminIntegrity = lazy(() => import("@/pages/admin/AdminIntegrity"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));

const MarketingPanel = lazy(() => import("@/pages/MarketingPanel").then(m => ({ default: m.MarketingPanel })));
const InvestorPortal = lazy(() => import("@/pages/InvestorPortal").then(m => ({ default: m.InvestorPortal })));
const TraderCompareView = lazy(() => import("@/components/investor/TraderCompareView").then(m => ({ default: m.TraderCompareView })));
const IBPortal = lazy(() => import("@/pages/IBPortal").then(m => ({ default: m.IBPortal })));
const SelectRole = lazy(() => import("@/pages/SelectRole").then(m => ({ default: m.SelectRole })));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Terms = lazy(() => import("@/pages/Terms").then(m => ({ default: m.Terms })));
const Privacy = lazy(() => import("@/pages/Privacy").then(m => ({ default: m.Privacy })));
const Refund = lazy(() => import("@/pages/Refund").then(m => ({ default: m.Refund })));
const Pricing = lazy(() => import("@/pages/Pricing").then(m => ({ default: m.Pricing })));


// Account Center
const AccountCenter = lazy(() => import("@/pages/AccountCenter").then(m => ({ default: m.AccountCenter })));

const ProfileSection = lazy(() => import("@/components/account-center/ProfileSection").then(m => ({ default: m.ProfileSection })));
const SecuritySection = lazy(() => import("@/components/account-center/SecuritySection").then(m => ({ default: m.SecuritySection })));
const PlansCreditsSection = lazy(() => import("@/components/account-center/PlansCreditsSection").then(m => ({ default: m.PlansCreditsSection })));
const BillingPaymentsSection = lazy(() => import("@/components/account-center/BillingPaymentsSection").then(m => ({ default: m.BillingPaymentsSection })));
const TradingAccountsSection = lazy(() => import("@/components/account-center/TradingAccountsSection").then(m => ({ default: m.TradingAccountsSection })));
const PerformanceSection = lazy(() => import("@/components/account-center/PerformanceSection").then(m => ({ default: m.PerformanceSection })));
const PreferencesSection = lazy(() => import("@/components/account-center/PreferencesSection").then(m => ({ default: m.PreferencesSection })));
const PrivacySection = lazy(() => import("@/components/account-center/PrivacySection").then(m => ({ default: m.PrivacySection })));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

/** Renders the correct home page based on the user's role */
const RoleBasedHome = () => {
  const { role, loading } = useUserRole();
  if (loading) return <PageLoader />;
  if (role === "investor") return <InvestorPortal />;
  if (role === "ib") return <RoleProtectedRoute allowedRoles={["ib", "admin"]}><IBPortal /></RoleProtectedRoute>;
  return <Dashboard />;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/auth" element={<Navigate to="/" replace />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/refund" element={<Refund />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                  <Route path="/select-role" element={<ProtectedRoute><SelectRole /></ProtectedRoute>} />
                  <Route path="/trader/:username" element={<PublicProfile />} />
                  <Route
                    path="/*"
                    element={
                      <RootGate>
                        <TourGuideProvider>
                          <Layout>
                            <TourTooltip />
                            <Routes>
                            <Route path="/" element={<RoleBasedHome />} />
                            <Route path="/trades" element={<Trades />} />
                            <Route path="/trades/:id" element={<TradeDetail />} />
                            <Route path="/analytics" element={<Analytics />} />
                            <Route path="/market-intelligence" element={<MarketIntelligencePage />} />
                            <Route path="/calendar" element={<Navigate to="/market-intelligence?tab=calendar" replace />} />
                            <Route path="/training" element={<Training />} />
                            <Route path="/training/plans/new" element={<TradingPlanEditor />} />
                            <Route path="/training/plans/:id" element={<TradingPlanEditor />} />
                            <Route path="/training/blueprint" element={<TradeBlueprintPage />} />
                            <Route path="/training/blueprint/new" element={<BlueprintEditor />} />
                            <Route path="/training/blueprint/:id" element={<BlueprintEditor />} />
                            <Route path="/training/strategies/new" element={<JrxStrategyProducerPage />} />
                            <Route path="/training/strategies/edit/:id" element={<JrxStrategyProducerPage />} />
                            <Route path="/training/my-setups/:id" element={<UserSetupDetail />} />
                            <Route path="/training/behavior/:id" element={<BehaviorProfileEditor />} />
                            <Route path="/training/execution/:id" element={<ExecutionProfileEditor />} />
                            <Route path="/training/risk-profiles/new" element={<RiskProfileEditor />} />
                            <Route path="/training/risk-profiles/:id" element={<RiskProfileEditor />} />
                            <Route path="/training/strategies/:slug" element={<TrainingDetail />} />
                            <Route path="/training/setups/:slug" element={<TrainingDetail />} />
                            <Route path="/training/indicators/:slug" element={<TrainingDetail />} />
                            <Route path="/fundamentals" element={<Fundamentals />} />
                            <Route path="/assistant" element={<TradeAssistant />} />

                            {/* Account Center */}
                            <Route path="/account/*" element={<AccountCenter />}>
                              <Route index element={<Navigate to="/account/profile" replace />} />
                              <Route path="overview" element={<Navigate to="/account/profile" replace />} />
                              <Route path="profile" element={<ProfileSection />} />
                              <Route path="security" element={<Navigate to="/account/preferences" replace />} />
                              <Route path="billing" element={<PlansCreditsSection />} />
                              <Route path="credits" element={<Navigate to="/account/billing" replace />} />
                              <Route path="payments" element={<BillingPaymentsSection />} />
                              <Route path="trading" element={<TradingAccountsSection />} />
                              <Route path="performance" element={<Navigate to="/account/profile" replace />} />
                              <Route path="preferences" element={<PreferencesSection />} />
                              <Route path="privacy" element={<PrivacySection />} />
                            </Route>

                            {/* Redirects from old routes */}
                            <Route path="/settings" element={<Navigate to="/account/preferences" replace />} />
                            <Route path="/settings/risk" element={<Navigate to="/training" replace />} />
                            <Route path="/connections" element={<Navigate to="/account/trading" replace />} />

                            {/* Role-based portals */}
                            <Route path="/investor" element={<RoleProtectedRoute allowedRoles={["investor", "admin"]}><InvestorPortal /></RoleProtectedRoute>} />
                            <Route path="/investor/compare" element={<RoleProtectedRoute allowedRoles={["investor", "admin"]}><TraderCompareView /></RoleProtectedRoute>} />
                            <Route path="/ib" element={<RoleProtectedRoute allowedRoles={["ib", "admin"]}><IBPortal /></RoleProtectedRoute>} />

                            {/* Admin Workspace */}
                            <Route path="/admin/*" element={<RoleProtectedRoute allowedRoles={["admin"]}><AdminWorkspaceLayout /></RoleProtectedRoute>}>
                              <Route index element={<AdminOverview />} />
                              <Route path="users" element={<AdminUsers />} />
                              <Route path="users/:id" element={<AdminUserDetail />} />
                              <Route path="commercial/*" element={<CommercialCenter />}>
                                <Route index element={<CommercialOverview />} />
                                <Route path="promotions" element={<CommercialPromotions />} />
                                <Route path="promotions/new" element={<CommercialPromoForm />} />
                                <Route path="promotions/:id" element={<CommercialPromoForm />} />
                                <Route path="vouchers" element={<CommercialVouchers />} />
                                <Route path="discounts" element={<CommercialDiscounts />} />
                                <Route path="redemptions" element={<CommercialRedemptions />} />
                                <Route path="referrals" element={<CommercialReferrals />} />
                                <Route path="local-payments" element={<CommercialLocalPayments />} />
                              </Route>
                              <Route path="billing" element={<AdminBilling />} />
                              <Route path="product" element={<AdminProduct />} />
                              <Route path="integrity" element={<AdminIntegrity />} />
                              <Route path="settings" element={<AdminSettings />} />
                            </Route>

                            <Route path="/marketing" element={<RoleProtectedRoute allowedRoles={["admin"]}><MarketingPanel /></RoleProtectedRoute>} />
                            <Route path="*" element={<NotFound />} />
                            </Routes>
                          </Layout>
                        </TourGuideProvider>
                      </RootGate>
                    }
                  />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;
